import { cn } from "@/lib/utils";
import { AlertTriangle, CheckCircle, Circle } from "lucide-react";
import { forwardRef } from "react";

export type TimelineState = "pending" | "active" | "completed" | "invalidated";

interface TimelineProps {
  children: React.ReactNode;
  className?: string;
}

interface TimelineItemProps {
  state: TimelineState;
  title: string;
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

const Timeline = forwardRef<HTMLDivElement, TimelineProps>(
  ({ children, className }, ref) => {
    return (
      <div ref={ref} className={cn("relative flex flex-col", className)}>
        {children}
      </div>
    );
  },
);

Timeline.displayName = "Timeline";

const getStateIcon = (state: TimelineState) => {
  switch (state) {
    case "completed":
      return <CheckCircle className="w-5 h-5 text-success" />;
    case "active":
      return <Circle className="w-5 h-5 text-primary fill-primary" />;
    case "invalidated":
      return <AlertTriangle className="w-5 h-5 text-warning" />;
    default:
      return <Circle className="w-5 h-5 text-muted-foreground" />;
  }
};

const getStateColors = (state: TimelineState) => {
  switch (state) {
    case "completed":
      return "text-success";
    case "active":
      return "text-primary";
    case "invalidated":
      return "text-warning";
    default:
      return "text-muted-foreground";
  }
};

const TimelineItem = forwardRef<HTMLDivElement, TimelineItemProps>(
  ({ state, title, children, onClick, className }, ref) => {
    const isClickable =
      onClick && (state === "completed" || state === "active");

    return (
      <div ref={ref} className={cn("relative flex gap-4 flex-1", className)}>
        {/* Timeline dot and line */}
        <div className="flex flex-col items-center mt-1">
          {getStateIcon(state)}
          {/* Connecting line - only show if not the last item */}
          <div className="w-px flex-1 bg-border mt-2" />
        </div>

        {/* Content */}
        <div className="flex-1 pb-8">
          <div
            className={cn(
              "text-lg font-semibold mb-4 transition-colors",
              getStateColors(state),
              isClickable && "hover:opacity-80 cursor-pointer",
            )}
          >
            {title}
          </div>
          <div className="space-y-4">{children}</div>
        </div>
      </div>
    );
  },
);

TimelineItem.displayName = "TimelineItem";

// Remove the connecting line from the last timeline item
const TimelineItemLast = forwardRef<HTMLDivElement, TimelineItemProps>(
  ({ state, title, children, onClick, className }, ref) => {
    const isClickable =
      onClick && (state === "completed" || state === "active");

    return (
      <div ref={ref} className={cn("relative flex gap-4", className)}>
        {/* Timeline dot (no connecting line for last item) */}
        <div className="flex flex-col items-center">
          <div className="flex items-center justify-center transition-colors mt-1">
            {getStateIcon(state)}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <h3
            className={cn(
              "text-lg font-semibold mb-4 transition-colors",
              getStateColors(state),
              isClickable && "hover:opacity-80 cursor-pointer",
            )}
            onClick={isClickable ? onClick : undefined}
            onKeyUp={
              isClickable
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      onClick?.();
                    }
                  }
                : undefined
            }
            role={isClickable ? "button" : undefined}
            tabIndex={isClickable ? 0 : undefined}
          >
            {title}
          </h3>
          <div className="space-y-4">{children}</div>
        </div>
      </div>
    );
  },
);

TimelineItemLast.displayName = "TimelineItemLast";

export { Timeline, TimelineItem, TimelineItemLast };
